/**
 * Brand & Styles page (UX plan §4.6, phase U4) — replaces the Platforms →
 * collections → presets dialog chain with one flat page:
 *
 * - Brand kit: facts (logo, colors, font feel, product line, do/don'ts,
 *   product-screenshot pack — F12) confirmed through a 3-step wizard
 *   (decision #7: ALWAYS confirm; auto-pulled values are pre-filled defaults,
 *   never silently applied). Stored in AdCreativePlatformSettings.brandKit.
 * - Visual styles: design collections as cards with 3-swatch previews; three
 *   creation paths (reference ads / funnel theme / describe in words) all
 *   feed the existing design-collection generation task.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SDialog, StyledTextField } from '@modules/ui-components';
import type {
  AdCreativeBrandKit,
  AdCreativeDesignCollection,
} from '@segmently/schema-models/ad-creative';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';

export const BRAND = {
  page: 'ad-creative-brand-page',
  back: 'ad-creative-brand-back',
  setupCta: 'ad-creative-brand-setup-cta',
  wizard: 'ad-creative-brand-wizard',
  wizardStep: (index: number) => `ad-creative-brand-wizard-step-${index}`,
  wizardNext: 'ad-creative-brand-wizard-next',
  wizardBack: 'ad-creative-brand-wizard-back',
  wizardConfirm: 'ad-creative-brand-wizard-confirm',
  kitStrip: 'ad-creative-brand-kit-strip',
  kitEdit: 'ad-creative-brand-kit-edit',
  stylesGrid: 'ad-creative-brand-styles',
  styleCard: (id: string) => `ad-creative-brand-style-${id}`,
  newStyle: 'ad-creative-brand-new-style',
  newStyleOption: (key: string) => `ad-creative-brand-new-style-${key}`,
  describeDialog: 'ad-creative-brand-describe-dialog',
  describeSubmit: 'ad-creative-brand-describe-submit',
  screenshotInput: 'ad-creative-brand-screenshot-input',
  screenshotRow: (index: number) => `ad-creative-brand-screenshot-${index}`,
} as const;

const WIZARD_STEPS = ['Logo & product', 'Colors & fonts', "Do / don'ts & product screenshots"] as const;

interface BrandStylesPageProps {
  projectId: string;
  client: AdCreativeApiClient;
  designCollections: AdCreativeDesignCollection[];
  onBack: () => void;
  onOpenStyle: (collectionId: string) => void;
  onRefresh: () => void;
}

export function BrandStylesPage({
  projectId,
  client,
  designCollections,
  onBack,
  onOpenStyle,
  onRefresh,
}: BrandStylesPageProps): React.ReactElement {
  const [brandKit, setBrandKit] = useState<AdCreativeBrandKit | null>(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newStyleAnchor, setNewStyleAnchor] = useState<HTMLElement | null>(null);
  const [describeOpen, setDescribeOpen] = useState<false | 'describe' | 'reference'>(false);
  const [describeText, setDescribeText] = useState('');
  const [generating, setGenerating] = useState(false);

  const defaultCollection = useMemo(
    () => designCollections.find((collection) => collection.isCurrent || collection.isDefault) ?? designCollections[0] ?? null,
    [designCollections],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void client.getPlatformSettings(projectId).then((response) => {
      if (cancelled) return;
      setBrandKit((response.data?.settings?.brandKit ?? null) as AdCreativeBrandKit | null);
    }).catch(() => undefined).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [client, projectId]);

  const confirmed = Boolean(brandKit?.confirmedAt);

  const saveBrandKit = async (kit: AdCreativeBrandKit) => {
    const response = await client.updatePlatformSettings(projectId, { brandKit: kit });
    if (!response.success) throw new Error(response.error || 'Failed to save brand kit');
    setBrandKit((response.data?.settings?.brandKit ?? kit) as AdCreativeBrandKit);
  };

  const generateStyle = async (sourceBrief: string, name: string) => {
    setGenerating(true);
    setError(null);
    try {
      const response = await client.generateDesignCollection({
        projectId,
        networkId: 'meta',
        name,
        sourceBrief,
        setCurrent: designCollections.length === 0,
      });
      if (!response.success) throw new Error(response.error || 'Failed to start style generation');
      setStatus('Style generation started — the new style card appears when the task completes.');
      window.setTimeout(onRefresh, 8_000);
      window.setTimeout(onRefresh, 25_000);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setGenerating(false);
      setDescribeOpen(false);
      setDescribeText('');
    }
  };

  return (
    <Stack spacing={3} data-testid={BRAND.page}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Button size="small" startIcon={<ArrowBackIcon />} data-testid={BRAND.back} onClick={onBack}>
          Create
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Brand & Styles</Typography>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {status && <Alert severity="success" onClose={() => setStatus(null)}>{status}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
      ) : confirmed && brandKit ? (
        <Box data-testid={BRAND.kitStrip} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap" useFlexGap>
            {brandKit.logoUrl ? (
              <Box component="img" src={brandKit.logoUrl} alt="logo" sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 1, border: 1, borderColor: 'divider' }} />
            ) : (
              <Box sx={{ width: 56, height: 56, borderRadius: 1, border: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.disabled">logo</Typography>
              </Box>
            )}
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{brandKit.productLine || 'Brand kit'}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                {(brandKit.palette ?? []).slice(0, 6).map((color) => (
                  <Box key={color} sx={{ width: 18, height: 18, borderRadius: 0.5, bgcolor: color, border: 1, borderColor: 'divider' }} title={color} />
                ))}
                {brandKit.fontFeel && <Chip size="small" variant="outlined" label={brandKit.fontFeel} />}
                <Chip size="small" variant="outlined" color="success" icon={<CheckIcon />} label={`Confirmed · ${brandKit.seededFrom ?? 'manual'}`} />
                {(brandKit.productScreenshots?.length ?? 0) > 0 && (
                  <Chip size="small" variant="outlined" label={`${brandKit.productScreenshots!.length} product screenshots`} />
                )}
              </Stack>
              {(brandKit.dontList?.length ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Don'ts: {brandKit.dontList!.join(' · ')}
                </Typography>
              )}
            </Stack>
            <Button size="small" data-testid={BRAND.kitEdit} onClick={() => setWizardOpen(true)}>Edit</Button>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2.5, textAlign: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Confirm your brand kit</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Three quick steps — everything is pre-filled from your project; you only confirm.
            Product screenshots anchor the AI so ads show your REAL product.
          </Typography>
          <Button variant="contained" size="small" data-testid={BRAND.setupCta} onClick={() => setWizardOpen(true)}>
            Set up brand kit
          </Button>
        </Box>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Visual styles</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          data-testid={BRAND.newStyle}
          disabled={generating}
          onClick={(event) => setNewStyleAnchor(event.currentTarget)}
        >
          New style
        </Button>
        <Menu anchorEl={newStyleAnchor} open={Boolean(newStyleAnchor)} onClose={() => setNewStyleAnchor(null)}>
          <MenuItem
            data-testid={BRAND.newStyleOption('reference')}
            onClick={() => {
              setNewStyleAnchor(null);
              setDescribeOpen('reference');
            }}
          >
            From reference ads (paste URLs)
          </MenuItem>
          <MenuItem
            data-testid={BRAND.newStyleOption('theme')}
            onClick={() => {
              setNewStyleAnchor(null);
              void generateStyle(
                'Derive the ad visual style from THIS PROJECT\'s funnel theme: reuse its palette, typography feel, corner radius and overall density so ads look native to the product.',
                'Funnel theme style',
              );
            }}
          >
            From funnel theme
          </MenuItem>
          <MenuItem
            data-testid={BRAND.newStyleOption('describe')}
            onClick={() => {
              setNewStyleAnchor(null);
              setDescribeOpen('describe');
            }}
          >
            Describe in words
          </MenuItem>
        </Menu>
      </Stack>

      <Box
        data-testid={BRAND.stylesGrid}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5 }}
      >
        {designCollections.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
            No styles yet — generation uses a generic paid-social style until you add one.
          </Typography>
        )}
        {designCollections.map((collection) => {
          const palette = Array.isArray(collection.styleAnchor?.palette)
            ? (collection.styleAnchor!.palette as string[])
            : ['#111827', '#F59E0B', '#FFFFFF'];
          return (
            <Box
              key={collection.id}
              data-testid={BRAND.styleCard(collection.id)}
              onClick={() => onOpenStyle(collection.id)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, cursor: 'pointer', '&:hover': { borderColor: 'primary.light' } }}
            >
              <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                {palette.slice(0, 3).map((color, index) => (
                  <Box key={`${color}-${index}`} sx={{ flex: 1, height: 34, borderRadius: 0.75, bgcolor: color, border: 1, borderColor: 'divider' }} />
                ))}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }} noWrap>
                  {collection.name || collection.id}
                </Typography>
                {(collection.isCurrent || collection.isDefault) && <Chip size="small" color="primary" variant="outlined" label="Default" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {`${collection.presets?.length ?? 0} creative-type presets`}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <BrandKitWizard
        open={wizardOpen}
        initial={brandKit}
        seedCollection={defaultCollection}
        onClose={() => setWizardOpen(false)}
        onConfirm={async (kit) => {
          try {
            await saveBrandKit(kit);
            setWizardOpen(false);
            setStatus('Brand kit confirmed — generation now uses it for style and product anchors.');
          } catch (nextError) {
            setError(nextError instanceof Error ? nextError.message : String(nextError));
          }
        }}
      />

      <SDialog
        open={Boolean(describeOpen)}
        onClose={() => setDescribeOpen(false)}
        title={describeOpen === 'reference' ? 'New style from reference ads' : 'Describe the style'}
        maxWidth="sm"
        fullWidth
        data-testid={BRAND.describeDialog}
      >
        <Stack spacing={1.5} sx={{ px: 3, pb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {describeOpen === 'reference'
              ? 'Paste URLs of winning ads (one per line) and optionally what to keep from them.'
              : 'Describe how the ads should look — palette mood, typography feel, composition density.'}
          </Typography>
          <StyledTextField
            multiline
            minRows={4}
            fullWidth
            value={describeText}
            onChange={(value) => setDescribeText(String(value))}
          />
          <Button
            variant="contained"
            size="small"
            data-testid={BRAND.describeSubmit}
            disabled={generating || !describeText.trim()}
            onClick={() => void generateStyle(
              describeOpen === 'reference'
                ? `Build the style from these reference ads (keep their strongest visual patterns):\n${describeText.trim()}`
                : describeText.trim(),
              describeOpen === 'reference' ? 'Reference ads style' : 'Described style',
            )}
          >
            {generating ? 'Starting…' : 'Generate style'}
          </Button>
        </Stack>
      </SDialog>
    </Stack>
  );
}

function BrandKitWizard({
  open,
  initial,
  seedCollection,
  onClose,
  onConfirm,
}: {
  open: boolean;
  initial: AdCreativeBrandKit | null;
  seedCollection: AdCreativeDesignCollection | null;
  onClose: () => void;
  onConfirm: (kit: AdCreativeBrandKit) => Promise<void>;
}): React.ReactElement {
  const [step, setStep] = useState(0);
  const [logoUrl, setLogoUrl] = useState('');
  const [productLine, setProductLine] = useState('');
  const [paletteText, setPaletteText] = useState('');
  const [fontFeel, setFontFeel] = useState('');
  const [doText, setDoText] = useState('');
  const [dontText, setDontText] = useState('');
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [screenshotDraft, setScreenshotDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    // Pre-filled DEFAULTS (decision #7): existing kit first, then the current
    // design collection's style anchor as the best available seed.
    const seedPalette = Array.isArray(seedCollection?.styleAnchor?.palette)
      ? (seedCollection!.styleAnchor!.palette as string[])
      : [];
    const seedTypography = typeof seedCollection?.styleAnchor?.typography === 'string'
      ? String(seedCollection?.styleAnchor?.typography)
      : '';
    setLogoUrl(initial?.logoUrl ?? '');
    setProductLine(initial?.productLine ?? '');
    setPaletteText((initial?.palette ?? seedPalette).join(', '));
    setFontFeel(initial?.fontFeel ?? seedTypography);
    setDoText((initial?.doList ?? []).join('\n'));
    setDontText((initial?.dontList ?? ['No invented testimonials or ratings', 'No fabricated statistics']).join('\n'));
    setScreenshots(initial?.productScreenshots ?? []);
    setScreenshotDraft('');
  }, [initial, open, seedCollection]);

  const confirm = async () => {
    setSaving(true);
    try {
      await onConfirm({
        logoUrl: logoUrl.trim() || undefined,
        productLine: productLine.trim() || undefined,
        palette: paletteText.split(/[,\n]/).map((entry) => entry.trim()).filter(Boolean),
        fontFeel: fontFeel.trim() || undefined,
        doList: doText.split('\n').map((entry) => entry.trim()).filter(Boolean),
        dontList: dontText.split('\n').map((entry) => entry.trim()).filter(Boolean),
        productScreenshots: screenshots,
        seededFrom: initial?.seededFrom ?? (seedCollection ? 'design_collection' : 'manual'),
        confirmedAt: new Date(),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SDialog open={open} onClose={onClose} title="Brand kit — confirm 3 steps" maxWidth="sm" fullWidth data-testid={BRAND.wizard}>
      <Stack spacing={2} sx={{ px: 3, pb: 3 }}>
        <Stepper activeStep={step}>
          {WIZARD_STEPS.map((label, index) => (
            <Step key={label} data-testid={BRAND.wizardStep(index)}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Stack spacing={1.5}>
            <StyledTextField label="Logo URL" size="small" fullWidth value={logoUrl} onChange={(value) => setLogoUrl(String(value))} />
            <StyledTextField
              label="What are we advertising (one line)"
              size="small"
              fullWidth
              value={productLine}
              onChange={(value) => setProductLine(String(value))}
            />
          </Stack>
        )}
        {step === 1 && (
          <Stack spacing={1.5}>
            <StyledTextField
              label="Brand colors (hex, comma-separated)"
              size="small"
              fullWidth
              value={paletteText}
              onChange={(value) => setPaletteText(String(value))}
            />
            <Stack direction="row" spacing={0.5}>
              {paletteText.split(/[,\n]/).map((entry) => entry.trim()).filter(Boolean).slice(0, 8).map((color, index) => (
                <Box key={`${color}-${index}`} sx={{ width: 22, height: 22, borderRadius: 0.5, bgcolor: color, border: 1, borderColor: 'divider' }} />
              ))}
            </Stack>
            <StyledTextField
              label="Typography feel (plain language)"
              size="small"
              fullWidth
              value={fontFeel}
              onChange={(value) => setFontFeel(String(value))}
            />
          </Stack>
        )}
        {step === 2 && (
          <Stack spacing={1.5}>
            <StyledTextField label="Do (one per line)" size="small" fullWidth multiline minRows={2} value={doText} onChange={(value) => setDoText(String(value))} />
            <StyledTextField label="Don't (one per line)" size="small" fullWidth multiline minRows={2} value={dontText} onChange={(value) => setDontText(String(value))} />
            <Typography variant="caption" color="text.secondary">
              Product screenshots (CDN URLs) — the AI renders your app ONLY from these, instead of inventing a generic UI.
            </Typography>
            {screenshots.map((url, index) => (
              <Stack key={url} direction="row" spacing={1} alignItems="center" data-testid={BRAND.screenshotRow(index)}>
                <Box component="img" src={url} alt={`screenshot ${index + 1}`} sx={{ width: 36, height: 64, objectFit: 'cover', borderRadius: 0.75, border: 1, borderColor: 'divider' }} />
                <Typography variant="caption" sx={{ flex: 1 }} noWrap>{url}</Typography>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setScreenshots((current) => current.filter((entry) => entry !== url))}
                >
                  Remove
                </Button>
              </Stack>
            ))}
            <Stack direction="row" spacing={1}>
              <StyledTextField
                label="Add screenshot URL"
                size="small"
                fullWidth
                value={screenshotDraft}
                data-testid={BRAND.screenshotInput}
                onChange={(value) => setScreenshotDraft(String(value))}
              />
              <Button
                size="small"
                disabled={!/^https?:\/\//.test(screenshotDraft.trim())}
                onClick={() => {
                  setScreenshots((current) => [...current, screenshotDraft.trim()]);
                  setScreenshotDraft('');
                }}
              >
                Add
              </Button>
            </Stack>
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between">
          <Button size="small" data-testid={BRAND.wizardBack} disabled={step === 0 || saving} onClick={() => setStep((current) => current - 1)}>
            Back
          </Button>
          {step < WIZARD_STEPS.length - 1 ? (
            <Button size="small" variant="contained" data-testid={BRAND.wizardNext} onClick={() => setStep((current) => current + 1)}>
              Looks right — next
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              data-testid={BRAND.wizardConfirm}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} /> : <CheckIcon />}
              onClick={() => void confirm()}
            >
              Confirm brand kit
            </Button>
          )}
        </Stack>
      </Stack>
    </SDialog>
  );
}
